import { portalApi } from '../../../api';

const AGENT_PORTAL_VERIFY_EMAIL = 'AGENT_PORTAL_VERIFY_EMAIL';
const AGENT_PORTAL_VERIFY_EMAIL_SUCCESS = 'AGENT_PORTAL_VERIFY_EMAIL_SUCCESS';
const AGENT_PORTAL_VERIFY_EMAIL_FAILURE = 'AGENT_PORTAL_VERIFY_EMAIL_FAILURE';

export const verifySubmitterEmail = (insuranceRequestId, email) => async dispatch => {
  dispatch({ type: AGENT_PORTAL_VERIFY_EMAIL, insuranceRequestId, email });

  try {
    const response = await portalApi.verifySubmitterEmail(insuranceRequestId, email);

    dispatch({ type: AGENT_PORTAL_VERIFY_EMAIL_SUCCESS, response });
    return response;
  } catch (error) {
    dispatch({ type: AGENT_PORTAL_VERIFY_EMAIL_FAILURE, error });
    return {
      error: 'We have sent a verification email to your inbox. '
        + 'Please click the link within the email to continue. '
        + 'If you don\'t see the email, please check your spam/junk folder'
    };
  }
};

const AGENT_PORTAL_VERIFY_OTP = 'AGENT_PORTAL_VERIFY_OTP';
const AGENT_PORTAL_VERIFY_OTP_SUCCESS = 'AGENT_PORTAL_VERIFY_OTP_SUCCESS';
const AGENT_PORTAL_VERIFY_OTP_FAILURE = 'AGENT_PORTAL_VERIFY_OTP_FAILURE';

export const verifyOTP = insuranceRequestId => async dispatch => {
  dispatch({ type: AGENT_PORTAL_VERIFY_OTP, insuranceRequestId });

  try {
    const response = await portalApi.verifyOTP(insuranceRequestId);

    dispatch({ type: AGENT_PORTAL_VERIFY_OTP_SUCCESS, response });
    return response;
  } catch (error) {
    dispatch({ type: AGENT_PORTAL_VERIFY_OTP_FAILURE, error });
    return { error: 'Oops. Looks like this link is expired. Click the button below to verify your email:' };
  }
};

const AGENT_PORTAL_GET_INSURANCE_REQUEST = 'AGENT_PORTAL_GET_INSURANCE_REQUEST';
const AGENT_PORTAL_GET_INSURANCE_REQUEST_SUCCESS = 'AGENT_PORTAL_GET_INSURANCE_REQUEST_SUCCESS';
const AGENT_PORTAL_GET_INSURANCE_REQUEST_FAILURE = 'AGENT_PORTAL_GET_INSURANCE_REQUEST_FAILURE';

export const getInsuranceRequest = id => async dispatch => {
  dispatch({ type: AGENT_PORTAL_GET_INSURANCE_REQUEST, id });

  try {
    const response = await portalApi.getInsuranceRequest(id);
    dispatch({ type: AGENT_PORTAL_GET_INSURANCE_REQUEST_SUCCESS, response });
  } catch (error) {
    dispatch({ type: AGENT_PORTAL_GET_INSURANCE_REQUEST_FAILURE, error });
  }
};

const AGENT_PORTAL_GET_INSURED = 'AGENT_PORTAL_GET_INSURED';
const AGENT_PORTAL_GET_INSURED_SUCCESS = 'AGENT_PORTAL_GET_INSURED_SUCCESS';
const AGENT_PORTAL_GET_INSURED_FAILURE = 'AGENT_PORTAL_GET_INSURED_FAILURE';

export const getInsuranceRequestInsured = insuranceRequestId => async dispatch => {
  dispatch({ type: AGENT_PORTAL_GET_INSURED, insuranceRequestId });

  try {
    const response = await portalApi.getInsuranceRequestInsured(insuranceRequestId);
    dispatch({ type: AGENT_PORTAL_GET_INSURED_SUCCESS, response });
  } catch (error) {
    dispatch({ type: AGENT_PORTAL_GET_INSURED_FAILURE, error });
  }
};

const AGENT_PORTAL_GET_REQUIREMENTS = 'AGENT_PORTAL_GET_REQUIREMENTS';
const AGENT_PORTAL_GET_REQUIREMENTS_SUCCESS = 'AGENT_PORTAL_GET_REQUIREMENTS_SUCCESS';
const AGENT_PORTAL_GET_REQUIREMENTS_FAILURE = 'AGENT_PORTAL_GET_REQUIREMENTS_FAILURE';

export const getRequirements = (insuranceRequestId, insuredId, jobId, recordId) => async (dispatch, getState) => {
  dispatch({ type: AGENT_PORTAL_GET_REQUIREMENTS, insuredId, jobId, recordId });

  try {
    const insured = await portalApi.getInsured(insuredId);
    const companyId = insured.company_id;

    const company = await portalApi.getInsuredCompany(insuranceRequestId, insured.insured_id);
    const companyPolicyTypes = await portalApi.getCompanyPolicyTypes(companyId, { deleted: true });

    const insuranceRequirementsPromise = portalApi.getRecordWithRequirements(recordId);
    const jobPromise = portalApi.getJob(jobId, { include: ['department.owner', 'owner'] });
    Promise.all([insuranceRequirementsPromise, jobPromise])
      .then(result => {
        dispatch({
          type: AGENT_PORTAL_GET_REQUIREMENTS_SUCCESS,
          insured,
          company,
          insuranceRequirements: result[0],
          job: result[1],
          policyTypes: companyPolicyTypes
        });
      }).catch(error => {
        dispatch({ type: AGENT_PORTAL_GET_REQUIREMENTS_FAILURE, error });
      });
  } catch (error) {
    dispatch({ type: AGENT_PORTAL_GET_REQUIREMENTS_FAILURE, error });
  }
};

const AGENT_PORTAL_PREP_AGENT_DOCUMENT = 'AGENT_PORTAL_PREP_AGENT_DOCUMENT';

export const prepareAgentDocument = (docFile, checkedDocTypes) => dispatch => {
  dispatch({ type: AGENT_PORTAL_PREP_AGENT_DOCUMENT, docFile, checkedDocTypes, previewLink: URL.createObjectURL(docFile) });
};

const AGENT_PORTAL_REMOVE_AGENT_DOCUMENT = 'AGENT_PORTAL_REMOVE_AGENT_DOCUMENT';

export const removeAgentDocument = docName => dispatch => {
  dispatch({ type: AGENT_PORTAL_REMOVE_AGENT_DOCUMENT, docName });
};

const AGENT_PORTAL_IS_LOADING = 'AGENT_PORTAL_IS_LOADING';

export const updateAgentPortalIsLoading = bool => dispatch => {
  dispatch({ type: AGENT_PORTAL_IS_LOADING, bool });
};

const AGENT_PORTAL_SET_USER = 'AGENT_PORTAL_SET_USER';

export const setAgentPortalUser = user => dispatch => {
  dispatch({ type: AGENT_PORTAL_SET_USER, user });
};

const DEFAULT_STATE = {
  verified: false,
  user: null,
  insuredId: null,
  insured: null,
  jobId: null,
  recordId: null,
  requirements: null,
  company: null,
  job: null,
  documents: null,
  isLoading: true,
  insuranceCompanies: {},
  policyTypes: null,
  // Counts consecutive AGENT_PORTAL_GET_INSURANCE_REQUEST_FAILUREs so the UI
  // can break out of the verify <-> submission bounce defensively (DEV-3087).
  // Reset to 0 on the next confirmed AGENT_PORTAL_GET_INSURANCE_REQUEST_SUCCESS.
  verifyFailureCount: 0
};

const agentPortalReducer = (state = DEFAULT_STATE, action) => {
  switch (action.type) {
    case AGENT_PORTAL_VERIFY_EMAIL:
      return state;
    case AGENT_PORTAL_VERIFY_EMAIL_SUCCESS:
      return {
        ...state,
        verified: true,
        user: action.response.user,
        insuredId: action.response.insuredId,
        jobId: action.response.jobId
      };
    case AGENT_PORTAL_VERIFY_EMAIL_FAILURE:
      return { ...state, verified: false, user: null };
    case AGENT_PORTAL_VERIFY_OTP:
      return { ...state, isLoading: true };
    case AGENT_PORTAL_VERIFY_OTP_SUCCESS:
      return {
        ...state,
        verified: true,
        user: action.response.user,
        isLoading: false
      };
    case AGENT_PORTAL_VERIFY_OTP_FAILURE:
      return { ...state, verified: false, user: null, isLoading: false };

    case AGENT_PORTAL_GET_INSURED_SUCCESS: {
      return {
        ...state,
        insured: action.response
      };
    }

    case AGENT_PORTAL_GET_INSURANCE_REQUEST_SUCCESS:
      return {
        ...state,
        insuredId: action.response.insuredId,
        jobId: action.response.jobId,
        recordId: action.response.insuranceRequirementId,
        verified: true,
        user: state?.user ?? action.response.user,
        verifyFailureCount: 0
      };
    case AGENT_PORTAL_GET_INSURANCE_REQUEST_FAILURE:
      return { ...state, verified: false, verifyFailureCount: state.verifyFailureCount + 1 };

    case AGENT_PORTAL_GET_REQUIREMENTS:
      return { ...state, isLoading: true };

    case AGENT_PORTAL_GET_REQUIREMENTS_SUCCESS:
      return {
        ...state,
        requirements: action.insuranceRequirements,
        certHolder: action.company,
        job: action.job,
        policyTypes: action.policyTypes,
        isLoading: false,
        insured: action.insured
      };

    case AGENT_PORTAL_GET_REQUIREMENTS_FAILURE:
      return { ...state, isLoading: false };

    case AGENT_PORTAL_PREP_AGENT_DOCUMENT: {
      const agentDoc = action.docFile;
      const checkedDocReqs = action.checkedDocTypes;

      const updatedDocumentRequirements = state.requirements.documentRequirements.map(docReq => {
        if (checkedDocReqs[docReq.insuranceDocumentTypeId]) {
          if (docReq?.linked) {
            return { ...docReq, linked: [...docReq.linked, agentDoc] };
          }

          return { ...docReq, linked: [agentDoc] };
        }

        return docReq;
      });
      return {
        ...state,
        documents: {
          ...state.documents,
          [agentDoc.name]: {
            ...agentDoc,
            preview: action.previewLink,
            linked: checkedDocReqs
          }
        },
        requirements: {
          ...state.requirements,
          documentRequirements: updatedDocumentRequirements
        }
      };
    }

    case AGENT_PORTAL_REMOVE_AGENT_DOCUMENT: {
      const filteredDocuments = { ...state.documents };
      delete filteredDocuments[action.docName];

      const updatedDocumentRequirements = state.requirements.documentRequirements.map(docReq => {
        if (docReq?.linked) {
          return { ...docReq, linked: docReq.linked.filter(linkedDoc => (linkedDoc.name !== action.docName)) };
        }

        return docReq;
      });

      return {
        ...state,
        documents: filteredDocuments,
        requirements: {
          ...state.requirements,
          documentRequirements: updatedDocumentRequirements
        }
      };
    }

    case AGENT_PORTAL_IS_LOADING:
      return {
        ...state,
        isLoading: action.bool
      };

    case AGENT_PORTAL_SET_USER:
      return {
        ...state,
        user: action.user
      };

    default:
      return state;
  }
};

export const selectAgentPortalVerified = state => state.agentPortal.verified;
export const selectVerifyFailureCount = state => state.agentPortal.verifyFailureCount;
export const selectAgentPortalUser = state => state.agentPortal.user;
export const selectInsuredId = state => state.agentPortal.insuredId;
export const selectJobId = state => state.agentPortal.jobId;
export const selectRecordId = state => state.agentPortal.recordId;
export const selectCompany = state => state.agentPortal.certHolder;
export const selectJob = state => state.agentPortal.job;
export const selectInsuranceRequirements = state => state.agentPortal.requirements;
export const selectInsuranceRequirementDocuments = state => state.agentPortal.requirements.documentRequirements;
export const selectAgentDocuments = state => state.agentPortal.documents;
export const selectPolicyTypes = state => state.agentPortal.policyTypes;
export const selectAgentPortalLoading = state => state.agentPortal.isLoading;
export const selectInsured = state => state.agentPortal.insured;
export const selectUser = state => state.agentPortal.user;
export const selectInsuredCompanyFeatures = state => state.agentPortal.certHolder?.features ?? [];

export default agentPortalReducer;
